// src/rms/verticals/presets.js
// ============================================================================
// ReserveZoo — Vertical Configuration Presets
// ============================================================================
// SOURCE OF TRUTH for all 12 verticals. A vertical is data, not code: nothing
// downstream of this file branches on vertical_key. Screens read fields,
// labels, workflow steps and rate defaults from here, so adding a 13th vertical
// means adding one object to VERTICAL_PRESETS and nothing else.
//
// The SQL seed in supabase/migrations/*_rms_vertical_presets.sql is GENERATED
// from this file — run `npm run rms:generate-preset-seed` after editing, never
// hand-edit the migration.
//
// Preset shape
// ------------
//   key              stable identifier, also the FK value on rms_operators
//   label            operator-facing name in the picker
//   tagline          one-line pitch shown on the picker card
//   occupancyModel   'discrete'   → reserve → check out → use → check in
//                    'continuous' → move in → occupy → move out (storage)
//   assetSchema      custom fields stored in rms_assets.custom_fields
//   terminology      label overrides so one engine speaks each trade's language
//   workflow         which steps/fields are required at each leg
//   rateDefaults     default pricing cadence and deposit guidance
//   usageMetrics     what "usage tracking" means here (may be none, or several)
//   adCategory       ad targeting signal — a marina ad, not a muffler ad
// ============================================================================

import { operationalDefaultsFor } from "./operationalDefaults";

/** Field types the asset-schema renderer understands. */
export const FIELD_TYPES = {
  TEXT: "text",
  NUMBER: "number",
  SELECT: "select",
  BOOLEAN: "boolean",
  DATE: "date",
  PERCENT: "percent",
  CHECKLIST: "checklist",
};

export const OCCUPANCY_DISCRETE = "discrete";
export const OCCUPANCY_CONTINUOUS = "continuous";

// ----------------------------------------------------------------------------
// Shared fragments — kept DRY so common concepts stay consistent across
// verticals without collapsing the differences that matter.
// ----------------------------------------------------------------------------

/**
 * One choice in a SELECT or CHECKLIST field.
 *
 * The value is what gets persisted on the asset or condition report; the label
 * is the only half that is ever translated. They are separate because they
 * used to be the same string, which meant translating "Automatic" would have
 * silently orphaned every vehicle already stored with that value -- and a
 * member of staff switching language would have watched their own fleet data
 * stop matching. Values are lowercase and stable: never edit one to fix
 * wording, only the label.
 */
const opt = (value, label) => ({ value, label });

/** The option list declared for a field in this vertical, or [] if it has none. */
export function optionsForField(config, fieldKey) {
  const field = (config?.assetSchema || []).find((f) => f.key === fieldKey);
  return field?.options || [];
}

/**
 * Display label for a stored option value.
 *
 * Never returns undefined: an option removed from a preset after an asset was
 * saved still has to render as something, and showing the raw stored value is
 * more useful to whoever has to fix it than showing a blank. Also accepts the
 * legacy plain-string shape, because operator-authored checkout questions
 * still store their options that way and go through the same Select.
 */
export function optionLabel(options, value) {
  if (value === undefined || value === null || value === "") return "";
  for (const o of options || []) {
    if (typeof o === "string") {
      if (o === value) return o;
    } else if (o?.value === value) {
      return o.label;
    }
  }
  return String(value);
}

const FUEL_LEVEL_FIELD = {
  key: "fuel_level",
  label: "Fuel level",
  type: FIELD_TYPES.SELECT,
  options: [
          opt("empty", "Empty"),
          opt("1_4", "1/4"),
          opt("1_2", "1/2"),
          opt("3_4", "3/4"),
          opt("full", "Full"),
        ],
  capturedAt: ["checkout", "checkin"],
};

const CLEANLINESS_FIELD = {
  key: "cleanliness",
  label: "Cleanliness",
  type: FIELD_TYPES.SELECT,
  options: [
          opt("clean", "Clean"),
          opt("light_soil", "Light soil"),
          opt("needs_cleaning", "Needs cleaning"),
          opt("heavy_soil", "Heavy soil"),
        ],
  capturedAt: ["checkin"],
};

/** Terminology every vertical starts from; presets override what differs. */
const BASE_TERMINOLOGY = {
  assetSingular: "Asset",
  assetPlural: "Assets",
  assetCode: "Asset ID",
  reservation: "Reservation",
  reservationPlural: "Reservations",
  agreement: "Rental agreement",
  checkout: "Check out",
  checkin: "Check in",
  customer: "Customer",
  customerPlural: "Customers",
  usageLabel: "Usage",
  fleet: "Inventory",
  deposit: "Security deposit",
};

/** Workflow defaults; presets tighten or relax individual rules. */
const BASE_WORKFLOW = {
  idVerificationRequired: true,
  waiverRequired: false,
  conditionReportRequired: true,
  photosRequiredAtCheckout: false,
  photosRequiredAtCheckin: false,
  signatureRequiredAtCheckout: true,
  signatureRequiredAtCheckin: true,
  depositRequired: false,
  certificationRequired: false,
  deliveryEnabled: false,
  // Continuous-occupancy concerns; ignored by discrete verticals.
  accessControlTracking: false,
  recurringBilling: false,
};

const preset = (config) => ({
  ...config,
  occupancyModel: config.occupancyModel || OCCUPANCY_DISCRETE,
  terminology: { ...BASE_TERMINOLOGY, ...(config.terminology || {}) },
  workflow: { ...BASE_WORKFLOW, ...(config.workflow || {}) },
  usageMetrics: config.usageMetrics || [],
});

// ============================================================================
// The 12 verticals
// ============================================================================

export const VERTICAL_PRESETS = [
  // --------------------------------------------------------------------------
  preset({
    key: "car_rental",
    label: "Car Rentals",
    tagline: "Cars, vans and light trucks rented by the day.",
    icon: "car",
    adCategory: "automotive",
    terminology: {
      assetSingular: "Vehicle",
      assetPlural: "Vehicles",
      assetCode: "Unit #",
      fleet: "Fleet",
      usageLabel: "Mileage",
    },
    assetSchema: [
      { key: "make", label: "Make", type: FIELD_TYPES.TEXT, required: true },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT, required: true },
      { key: "year", label: "Year", type: FIELD_TYPES.NUMBER, required: true },
      { key: "vin", label: "VIN", type: FIELD_TYPES.TEXT, required: true, help: "17-character vehicle identification number" },
      { key: "license_plate", label: "License plate", type: FIELD_TYPES.TEXT, required: true },
      { key: "color", label: "Color", type: FIELD_TYPES.TEXT },
      { key: "transmission", label: "Transmission", type: FIELD_TYPES.SELECT, options: [opt("automatic", "Automatic"), opt("manual", "Manual")] },
      { key: "fuel_type", label: "Fuel type", type: FIELD_TYPES.SELECT, options: [
          opt("gasoline", "Gasoline"),
          opt("diesel", "Diesel"),
          opt("hybrid", "Hybrid"),
          opt("electric", "Electric"),
        ] },
      { key: "seats", label: "Seats", type: FIELD_TYPES.NUMBER },
      { key: "odometer", label: "Odometer", type: FIELD_TYPES.NUMBER, unit: "km", capturedAt: ["checkout", "checkin"] },
      FUEL_LEVEL_FIELD,
      CLEANLINESS_FIELD,
    ],
    usageMetrics: [
      { key: "odometer", label: "Mileage", unit: "km", altUnit: "mi", billable: true, includedPerDay: 200, overageCentsPerUnit: 25 },
    ],
    workflow: {
      idVerificationRequired: true,
      idFields: ["license_number", "license_expiry", "license_country"],
      depositRequired: true,
      photosRequiredAtCheckout: true,
      photosRequiredAtCheckin: true,
    },
    rateDefaults: { primaryUnit: "day", units: ["hour", "day", "week", "month"], suggestedDepositCents: 25000 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "boat_rental",
    label: "Boat Rentals",
    tagline: "Powerboats, pontoons and sailboats by the hour or day.",
    icon: "boat",
    adCategory: "marine",
    terminology: {
      assetSingular: "Vessel",
      assetPlural: "Vessels",
      assetCode: "Vessel #",
      fleet: "Fleet",
      usageLabel: "Engine hours",
      checkout: "Departure",
      checkin: "Return",
    },
    assetSchema: [
      { key: "make", label: "Make", type: FIELD_TYPES.TEXT, required: true },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT },
      { key: "year", label: "Year", type: FIELD_TYPES.NUMBER },
      { key: "hull_id", label: "Hull ID (HIN)", type: FIELD_TYPES.TEXT, required: true },
      { key: "registration_number", label: "Registration #", type: FIELD_TYPES.TEXT, required: true },
      { key: "length_ft", label: "Length", type: FIELD_TYPES.NUMBER, unit: "ft" },
      { key: "engine_type", label: "Engine type", type: FIELD_TYPES.SELECT, options: [
          opt("outboard", "Outboard"),
          opt("inboard", "Inboard"),
          opt("sterndrive", "Sterndrive"),
          opt("sail_auxiliary", "Sail / auxiliary"),
          opt("electric", "Electric"),
        ] },
      { key: "engine_hours", label: "Engine hours", type: FIELD_TYPES.NUMBER, unit: "hrs", capturedAt: ["checkout", "checkin"] },
      { key: "capacity_persons", label: "Max persons", type: FIELD_TYPES.NUMBER, required: true },
      { key: "trailer_included", label: "Trailer included", type: FIELD_TYPES.BOOLEAN },
      { key: "safety_equipment", label: "Safety equipment aboard", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("life_jackets", "Life jackets"),
          opt("flares", "Flares"),
          opt("fire_extinguisher", "Fire extinguisher"),
          opt("horn", "Horn"),
          opt("throwable_device", "Throwable device"),
          opt("first_aid_kit", "First aid kit"),
        ],
        capturedAt: ["checkout", "checkin"] },
      FUEL_LEVEL_FIELD,
    ],
    usageMetrics: [
      { key: "engine_hours", label: "Engine hours", unit: "hrs", billable: true, includedPerDay: 8, overageCentsPerUnit: 4500 },
    ],
    workflow: {
      waiverRequired: true,
      depositRequired: true,
      idVerificationRequired: true,
      idFields: ["boating_license_number", "license_expiry"],
      photosRequiredAtCheckout: true,
      photosRequiredAtCheckin: true,
      preDepartureBriefingRequired: true,
    },
    rateDefaults: { primaryUnit: "half_day", units: ["hour", "half_day", "day", "week"], suggestedDepositCents: 50000 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "atv_rental",
    label: "ATV & Off-Road Rentals",
    tagline: "ATVs, UTVs and side-by-sides for trail and dune use.",
    icon: "atv",
    adCategory: "powersports",
    terminology: {
      assetSingular: "Machine",
      assetPlural: "Machines",
      assetCode: "Machine #",
      fleet: "Fleet",
      usageLabel: "Ride hours",
    },
    assetSchema: [
      { key: "make", label: "Make", type: FIELD_TYPES.TEXT, required: true },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT, required: true },
      { key: "year", label: "Year", type: FIELD_TYPES.NUMBER },
      { key: "vin", label: "VIN / serial", type: FIELD_TYPES.TEXT, required: true },
      { key: "engine_cc", label: "Engine displacement", type: FIELD_TYPES.NUMBER, unit: "cc" },
      { key: "machine_type", label: "Type", type: FIELD_TYPES.SELECT, options: [
          opt("atv", "ATV"),
          opt("utv_side_by_side", "UTV / side-by-side"),
          opt("dirt_bike", "Dirt bike"),
          opt("dune_buggy", "Dune buggy"),
        ] },
      { key: "seats", label: "Seats", type: FIELD_TYPES.NUMBER },
      { key: "engine_hours", label: "Engine hours", type: FIELD_TYPES.NUMBER, unit: "hrs", capturedAt: ["checkout", "checkin"] },
      { key: "helmets_issued", label: "Helmets issued", type: FIELD_TYPES.NUMBER, capturedAt: ["checkout", "checkin"] },
      { key: "gear_issued", label: "Safety gear issued", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("helmet", "Helmet"),
          opt("goggles", "Goggles"),
          opt("gloves", "Gloves"),
          opt("chest_protector", "Chest protector"),
          opt("boots", "Boots"),
        ],
        capturedAt: ["checkout", "checkin"] },
      FUEL_LEVEL_FIELD,
    ],
    usageMetrics: [
      { key: "engine_hours", label: "Ride hours", unit: "hrs", billable: true, includedPerDay: 6, overageCentsPerUnit: 3500 },
    ],
    workflow: {
      waiverRequired: true,
      depositRequired: true,
      photosRequiredAtCheckout: true,
      photosRequiredAtCheckin: true,
      safetyBriefingRequired: true,
      minimumAgeCheck: true,
    },
    rateDefaults: { primaryUnit: "half_day", units: ["hour", "half_day", "day"], suggestedDepositCents: 40000 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "bicycle_rental",
    label: "Bicycle Rentals",
    tagline: "City bikes, e-bikes and kids' bikes by the hour.",
    icon: "bike",
    adCategory: "cycling",
    terminology: {
      assetSingular: "Bike",
      assetPlural: "Bikes",
      assetCode: "Bike #",
      fleet: "Fleet",
      usageLabel: "Ride time",
      agreement: "Rental ticket",
    },
    assetSchema: [
      { key: "bike_type", label: "Type", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("city_hybrid", "City / hybrid"),
          opt("road", "Road"),
          opt("mountain", "Mountain"),
          opt("e_bike", "E-bike"),
          opt("kids", "Kids"),
          opt("tandem", "Tandem"),
          opt("cargo", "Cargo"),
        ] },
      { key: "frame_size", label: "Frame size", type: FIELD_TYPES.SELECT, required: true, options: [opt("xs", "XS"), opt("s", "S"), opt("m", "M"), opt("l", "L"), opt("xl", "XL")] },
      { key: "wheel_size", label: "Wheel size", type: FIELD_TYPES.TEXT, unit: "in" },
      { key: "gear_count", label: "Gears", type: FIELD_TYPES.NUMBER },
      { key: "is_ebike", label: "Electric assist", type: FIELD_TYPES.BOOLEAN },
      { key: "battery_charge", label: "Battery charge", type: FIELD_TYPES.PERCENT, capturedAt: ["checkout", "checkin"],
        showIf: { field: "is_ebike", equals: true } },
      { key: "lock_code", label: "Lock combination", type: FIELD_TYPES.TEXT, sensitive: true },
      { key: "accessories", label: "Accessories issued", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("helmet", "Helmet"),
          opt("lock", "Lock"),
          opt("lights", "Lights"),
          opt("basket", "Basket"),
          opt("child_seat", "Child seat"),
          opt("pump", "Pump"),
        ],
        capturedAt: ["checkout", "checkin"] },
    ],
    usageMetrics: [
      { key: "ride_time", label: "Ride time", unit: "hrs", billable: false },
      { key: "battery_cycles", label: "Battery cycles", unit: "cycles", billable: false },
    ],
    workflow: {
      idVerificationRequired: true,
      waiverRequired: false,
      depositRequired: false,
      conditionReportRequired: true,
      signatureRequiredAtCheckin: false,
    },
    rateDefaults: { primaryUnit: "hour", units: ["hour", "half_day", "day", "week"], suggestedDepositCents: 0 },
  }),

  // --------------------------------------------------------------------------
  // STRUCTURALLY DISTINCT #1 — continuous occupancy, no trip concept.
  preset({
    key: "storage_unit",
    label: "Storage Lockers & Units",
    tagline: "Self-storage units billed monthly, occupied continuously.",
    icon: "storage",
    occupancyModel: OCCUPANCY_CONTINUOUS,
    adCategory: "self_storage",
    terminology: {
      assetSingular: "Unit",
      assetPlural: "Units",
      assetCode: "Unit #",
      fleet: "Facility",
      reservation: "Lease",
      reservationPlural: "Leases",
      agreement: "Rental agreement",
      checkout: "Move in",
      checkin: "Move out",
      customer: "Tenant",
      customerPlural: "Tenants",
      usageLabel: "Access activity",
      deposit: "Security deposit",
    },
    assetSchema: [
      { key: "unit_size", label: "Unit size", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("5x5", "5x5"),
          opt("5x10", "5x10"),
          opt("10x10", "10x10"),
          opt("10x15", "10x15"),
          opt("10x20", "10x20"),
          opt("10x30", "10x30"),
          opt("locker", "Locker"),
        ] },
      { key: "width_ft", label: "Width", type: FIELD_TYPES.NUMBER, unit: "ft" },
      { key: "depth_ft", label: "Depth", type: FIELD_TYPES.NUMBER, unit: "ft" },
      { key: "height_ft", label: "Ceiling height", type: FIELD_TYPES.NUMBER, unit: "ft" },
      { key: "floor", label: "Floor", type: FIELD_TYPES.SELECT, options: [
          opt("ground", "Ground"),
          opt("2nd", "2nd"),
          opt("3rd", "3rd"),
          opt("basement", "Basement"),
        ] },
      { key: "climate_controlled", label: "Climate controlled", type: FIELD_TYPES.BOOLEAN },
      { key: "drive_up_access", label: "Drive-up access", type: FIELD_TYPES.BOOLEAN },
      { key: "door_type", label: "Door type", type: FIELD_TYPES.SELECT, options: [opt("roll_up", "Roll-up"), opt("swing", "Swing"), opt("locker_door", "Locker door")] },
      { key: "gate_zone", label: "Gate / building zone", type: FIELD_TYPES.TEXT },
      { key: "access_code", label: "Gate access code", type: FIELD_TYPES.TEXT, sensitive: true,
        help: "Issued to the tenant at move-in and revoked at move-out." },
    ],
    // No usage metering — there is no trip. Access events replace it.
    usageMetrics: [],
    workflow: {
      idVerificationRequired: true,
      waiverRequired: false,
      // A tenant's own goods are not inspected on the way out; the unit is.
      conditionReportRequired: false,
      photosRequiredAtCheckout: false,
      photosRequiredAtCheckin: true, // unit condition at move-out
      signatureRequiredAtCheckin: false,
      depositRequired: true,
      accessControlTracking: true,
      recurringBilling: true,
      lienTracking: true, // delinquency → lien process is table stakes in storage
    },
    rateDefaults: {
      primaryUnit: "month",
      units: ["month"],
      suggestedDepositCents: 5000,
      billingDayOfMonth: 1,
      prorateFirstMonth: true,
      lateFeeGraceDays: 5,
      lateFeeCents: 2000,
    },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "tool_rental",
    label: "Tool Rental",
    tagline: "Power tools and hand tools rented by the hour or day.",
    icon: "tool",
    adCategory: "tools_hardware",
    terminology: {
      assetSingular: "Tool",
      assetPlural: "Tools",
      assetCode: "Tool #",
      fleet: "Inventory",
      usageLabel: "Runtime",
      agreement: "Rental ticket",
    },
    assetSchema: [
      { key: "tool_type", label: "Tool type", type: FIELD_TYPES.TEXT, required: true },
      { key: "manufacturer", label: "Manufacturer", type: FIELD_TYPES.TEXT },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT },
      { key: "power_source", label: "Power source", type: FIELD_TYPES.SELECT,
        options: [
          opt("corded_electric", "Corded electric"),
          opt("battery", "Battery"),
          opt("gas", "Gas"),
          opt("pneumatic", "Pneumatic"),
          opt("hydraulic", "Hydraulic"),
          opt("manual", "Manual"),
        ] },
      { key: "voltage", label: "Voltage / size", type: FIELD_TYPES.TEXT },
      { key: "battery_charge", label: "Battery charge", type: FIELD_TYPES.PERCENT, capturedAt: ["checkout", "checkin"],
        showIf: { field: "power_source", equals: "Battery" } },
      { key: "runtime_hours", label: "Runtime hours", type: FIELD_TYPES.NUMBER, unit: "hrs", capturedAt: ["checkout", "checkin"] },
      { key: "accessories", label: "Accessory checklist", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("case", "Case"),
          opt("charger", "Charger"),
          opt("spare_battery", "Spare battery"),
          opt("blade_bit_set", "Blade / bit set"),
          opt("manual", "Manual"),
          opt("guard", "Guard"),
          opt("hose", "Hose"),
        ],
        capturedAt: ["checkout", "checkin"] },
      { key: "consumables_included", label: "Consumables included", type: FIELD_TYPES.TEXT },
    ],
    usageMetrics: [
      { key: "runtime_hours", label: "Runtime", unit: "hrs", billable: false },
    ],
    workflow: {
      idVerificationRequired: true,
      waiverRequired: false,
      depositRequired: true,
      photosRequiredAtCheckin: true,
    },
    rateDefaults: { primaryUnit: "day", units: ["hour", "half_day", "day", "week"], suggestedDepositCents: 10000 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "party_equipment",
    label: "Party & Event Equipment",
    tagline: "Tents, tables, chairs, linens and sound systems.",
    icon: "party",
    adCategory: "events_hospitality",
    terminology: {
      assetSingular: "Item",
      assetPlural: "Items",
      assetCode: "Item #",
      fleet: "Inventory",
      reservation: "Event booking",
      reservationPlural: "Event bookings",
      usageLabel: "Usage",
      checkout: "Dispatch",
      checkin: "Return",
    },
    assetSchema: [
      { key: "item_type", label: "Item type", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("tent_marquee", "Tent / marquee"),
          opt("table", "Table"),
          opt("chair", "Chair"),
          opt("linen", "Linen"),
          opt("sound_system", "Sound system"),
          opt("lighting", "Lighting"),
          opt("staging", "Staging"),
          opt("dance_floor", "Dance floor"),
          opt("heater", "Heater"),
          opt("bar", "Bar"),
          opt("tableware", "Tableware"),
        ] },
      { key: "quantity_in_set", label: "Quantity in this set", type: FIELD_TYPES.NUMBER, required: true, default: 1,
        help: "Bulk items are tracked as a set — 100 chairs can be one asset record." },
      { key: "dimensions", label: "Dimensions", type: FIELD_TYPES.TEXT },
      { key: "capacity", label: "Seats / capacity", type: FIELD_TYPES.NUMBER },
      { key: "color", label: "Color / finish", type: FIELD_TYPES.TEXT },
      { key: "setup_required", label: "Requires crew setup", type: FIELD_TYPES.BOOLEAN },
      { key: "setup_time_minutes", label: "Setup time", type: FIELD_TYPES.NUMBER, unit: "min" },
      { key: "pieces_out", label: "Pieces dispatched", type: FIELD_TYPES.NUMBER, capturedAt: ["checkout"] },
      { key: "pieces_returned", label: "Pieces returned", type: FIELD_TYPES.NUMBER, capturedAt: ["checkin"] },
      CLEANLINESS_FIELD,
    ],
    usageMetrics: [],
    workflow: {
      idVerificationRequired: true,
      depositRequired: true,
      deliveryEnabled: true,
      photosRequiredAtCheckin: true,
      countReconciliationRequired: true, // pieces out must reconcile with pieces back
    },
    rateDefaults: { primaryUnit: "day", units: ["day", "week"], suggestedDepositCents: 15000 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "photo_av_gear",
    label: "Photography & AV Gear",
    tagline: "Cameras, lenses, lighting and audio kit for productions.",
    icon: "camera",
    adCategory: "photography_av",
    terminology: {
      assetSingular: "Kit item",
      assetPlural: "Kit",
      assetCode: "Asset tag",
      fleet: "Inventory",
      usageLabel: "Shutter count",
      agreement: "Rental agreement",
    },
    assetSchema: [
      { key: "gear_type", label: "Gear type", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("camera_body", "Camera body"),
          opt("lens", "Lens"),
          opt("lighting", "Lighting"),
          opt("audio", "Audio"),
          opt("tripod_support", "Tripod / support"),
          opt("gimbal", "Gimbal"),
          opt("drone", "Drone"),
          opt("monitor", "Monitor"),
          opt("grip", "Grip"),
          opt("accessory", "Accessory"),
        ] },
      { key: "brand", label: "Brand", type: FIELD_TYPES.TEXT, required: true },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT, required: true },
      { key: "serial_number", label: "Serial number", type: FIELD_TYPES.TEXT, required: true },
      { key: "mount", label: "Mount / connector", type: FIELD_TYPES.TEXT },
      { key: "shutter_count", label: "Shutter actuations", type: FIELD_TYPES.NUMBER, capturedAt: ["checkout", "checkin"],
        showIf: { field: "gear_type", equals: "Camera body" } },
      { key: "replacement_value_cents", label: "Replacement value", type: FIELD_TYPES.NUMBER, unit: "cents",
        help: "Drives the insurance/deposit requirement for this item." },
      { key: "case_included", label: "Case included", type: FIELD_TYPES.BOOLEAN },
      { key: "accessories", label: "Accessory checklist", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("battery", "Battery"),
          opt("charger", "Charger"),
          opt("memory_card", "Memory card"),
          opt("cable", "Cable"),
          opt("lens_cap", "Lens cap"),
          opt("hood", "Hood"),
          opt("strap", "Strap"),
          opt("case", "Case"),
        ],
        capturedAt: ["checkout", "checkin"] },
    ],
    usageMetrics: [
      { key: "shutter_count", label: "Shutter actuations", unit: "actuations", billable: false },
    ],
    workflow: {
      idVerificationRequired: true,
      depositRequired: true,
      insuranceCertificateRequired: true, // standard for high-value production gear
      photosRequiredAtCheckout: true,
      photosRequiredAtCheckin: true,
      serialVerificationRequired: true,
    },
    rateDefaults: { primaryUnit: "day", units: ["day", "week"], suggestedDepositCents: 100000 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "costume_formalwear",
    label: "Costume & Formalwear",
    tagline: "Tuxedos, gowns and costumes rented per event.",
    icon: "garment",
    adCategory: "apparel_formalwear",
    terminology: {
      assetSingular: "Garment",
      assetPlural: "Garments",
      assetCode: "Garment #",
      fleet: "Wardrobe",
      usageLabel: "Wear count",
      checkout: "Pick up",
      checkin: "Return",
    },
    assetSchema: [
      { key: "garment_type", label: "Garment type", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("tuxedo", "Tuxedo"),
          opt("suit", "Suit"),
          opt("gown", "Gown"),
          opt("dress", "Dress"),
          opt("costume", "Costume"),
          opt("accessory", "Accessory"),
          opt("shoes", "Shoes"),
          opt("outerwear", "Outerwear"),
        ] },
      { key: "size", label: "Size", type: FIELD_TYPES.TEXT, required: true },
      { key: "size_system", label: "Size system", type: FIELD_TYPES.SELECT, options: [
          opt("us", "US"),
          opt("uk", "UK"),
          opt("eu", "EU"),
          opt("numeric", "Numeric"),
          opt("alpha", "Alpha"),
        ] },
      { key: "color", label: "Color", type: FIELD_TYPES.TEXT },
      { key: "fabric", label: "Fabric", type: FIELD_TYPES.TEXT },
      { key: "alterations_notes", label: "Alterations on file", type: FIELD_TYPES.TEXT },
      { key: "cleaning_status", label: "Cleaning status", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("ready", "Ready"),
          opt("at_cleaner", "At cleaner"),
          opt("needs_cleaning", "Needs cleaning"),
          opt("needs_repair", "Needs repair"),
          opt("retired", "Retired"),
        ],
        capturedAt: ["checkin"] },
      { key: "wear_count", label: "Times worn", type: FIELD_TYPES.NUMBER },
      { key: "pieces", label: "Pieces in set", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("jacket", "Jacket"),
          opt("trousers", "Trousers"),
          opt("shirt", "Shirt"),
          opt("vest", "Vest"),
          opt("tie_bow_tie", "Tie / bow tie"),
          opt("cummerbund", "Cummerbund"),
          opt("shoes", "Shoes"),
          opt("cufflinks", "Cufflinks"),
        ],
        capturedAt: ["checkout", "checkin"] },
    ],
    usageMetrics: [
      { key: "wear_count", label: "Wears", unit: "wears", billable: false },
    ],
    workflow: {
      idVerificationRequired: true,
      depositRequired: true,
      fittingAppointmentEnabled: true,
      photosRequiredAtCheckin: true,
      // Every return routes through cleaning before it can be rented again.
      cleaningCycleRequired: true,
    },
    rateDefaults: { primaryUnit: "day", units: ["day", "week"], suggestedDepositCents: 20000 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "camping_gear",
    label: "Camping & Outdoor Gear",
    tagline: "Tents, packs, sleeping bags and climbing kit.",
    icon: "tent",
    adCategory: "outdoor_recreation",
    terminology: {
      assetSingular: "Gear item",
      assetPlural: "Gear",
      assetCode: "Gear #",
      fleet: "Inventory",
      usageLabel: "Trips",
      checkout: "Pick up",
      checkin: "Return",
    },
    assetSchema: [
      { key: "gear_type", label: "Gear type", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("tent", "Tent"),
          opt("sleeping_bag", "Sleeping bag"),
          opt("sleeping_pad", "Sleeping pad"),
          opt("backpack", "Backpack"),
          opt("stove", "Stove"),
          opt("cooler", "Cooler"),
          opt("climbing", "Climbing"),
          opt("kayak_canoe", "Kayak / canoe"),
          opt("paddleboard", "Paddleboard"),
          opt("snowshoes", "Snowshoes"),
          opt("bear_canister", "Bear canister"),
        ] },
      { key: "brand", label: "Brand", type: FIELD_TYPES.TEXT },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT },
      { key: "capacity_persons", label: "Capacity", type: FIELD_TYPES.NUMBER, unit: "persons" },
      { key: "season_rating", label: "Season / temp rating", type: FIELD_TYPES.TEXT },
      { key: "weight_kg", label: "Weight", type: FIELD_TYPES.NUMBER, unit: "kg" },
      { key: "trip_count", label: "Trips completed", type: FIELD_TYPES.NUMBER },
      { key: "cleaning_status", label: "Cleaning status", type: FIELD_TYPES.SELECT,
        options: [
          opt("ready", "Ready"),
          opt("needs_cleaning", "Needs cleaning"),
          opt("needs_drying", "Needs drying"),
          opt("needs_repair", "Needs repair"),
          opt("retired", "Retired"),
        ], capturedAt: ["checkin"] },
      { key: "components", label: "Component checklist", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("poles", "Poles"),
          opt("stakes", "Stakes"),
          opt("rainfly", "Rainfly"),
          opt("footprint", "Footprint"),
          opt("stuff_sack", "Stuff sack"),
          opt("fuel_canister", "Fuel canister"),
          opt("repair_kit", "Repair kit"),
        ],
        capturedAt: ["checkout", "checkin"] },
    ],
    usageMetrics: [
      { key: "trip_count", label: "Trips", unit: "trips", billable: false },
    ],
    workflow: {
      idVerificationRequired: true,
      depositRequired: true,
      photosRequiredAtCheckin: true,
      cleaningCycleRequired: true,
    },
    rateDefaults: { primaryUnit: "day", units: ["day", "week"], suggestedDepositCents: 7500 },
  }),

  // --------------------------------------------------------------------------
  preset({
    key: "rv_rental",
    label: "RV & Camper Rentals",
    tagline: "Motorhomes, campervans and travel trailers.",
    icon: "rv",
    adCategory: "rv_travel",
    terminology: {
      assetSingular: "RV",
      assetPlural: "RVs",
      assetCode: "Unit #",
      fleet: "Fleet",
      usageLabel: "Mileage & generator hours",
    },
    assetSchema: [
      { key: "rv_class", label: "Class", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("class_a", "Class A"),
          opt("class_b_campervan", "Class B / campervan"),
          opt("class_c", "Class C"),
          opt("travel_trailer", "Travel trailer"),
          opt("fifth_wheel", "Fifth wheel"),
          opt("pop_up", "Pop-up"),
        ] },
      { key: "make", label: "Make", type: FIELD_TYPES.TEXT, required: true },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT, required: true },
      { key: "year", label: "Year", type: FIELD_TYPES.NUMBER },
      { key: "vin", label: "VIN", type: FIELD_TYPES.TEXT, required: true },
      { key: "license_plate", label: "License plate", type: FIELD_TYPES.TEXT, required: true },
      { key: "length_ft", label: "Length", type: FIELD_TYPES.NUMBER, unit: "ft" },
      { key: "sleeps", label: "Sleeps", type: FIELD_TYPES.NUMBER, required: true },
      { key: "odometer", label: "Odometer", type: FIELD_TYPES.NUMBER, unit: "km", capturedAt: ["checkout", "checkin"] },
      { key: "generator_hours", label: "Generator hours", type: FIELD_TYPES.NUMBER, unit: "hrs", capturedAt: ["checkout", "checkin"] },
      { key: "propane_level", label: "Propane level", type: FIELD_TYPES.SELECT, options: [
          opt("empty", "Empty"),
          opt("1_4", "1/4"),
          opt("1_2", "1/2"),
          opt("3_4", "3/4"),
          opt("full", "Full"),
        ], capturedAt: ["checkout", "checkin"] },
      { key: "fresh_water_level", label: "Fresh water", type: FIELD_TYPES.PERCENT, capturedAt: ["checkout", "checkin"] },
      { key: "waste_tank_status", label: "Waste tanks", type: FIELD_TYPES.SELECT,
        options: [
          opt("empty_dumped", "Empty / dumped"),
          opt("partial", "Partial"),
          opt("full_needs_dump", "Full — needs dump"),
        ], capturedAt: ["checkout", "checkin"] },
      FUEL_LEVEL_FIELD,
      CLEANLINESS_FIELD,
    ],
    usageMetrics: [
      { key: "odometer", label: "Mileage", unit: "km", altUnit: "mi", billable: true, includedPerDay: 150, overageCentsPerUnit: 45 },
      { key: "generator_hours", label: "Generator hours", unit: "hrs", billable: true, includedPerDay: 0, overageCentsPerUnit: 500 },
    ],
    workflow: {
      idVerificationRequired: true,
      idFields: ["license_number", "license_expiry", "license_class"],
      waiverRequired: true,
      depositRequired: true,
      photosRequiredAtCheckout: true,
      photosRequiredAtCheckin: true,
      orientationWalkthroughRequired: true,
      minimumAgeCheck: true,
    },
    rateDefaults: { primaryUnit: "day", units: ["day", "week", "month"], suggestedDepositCents: 150000 },
  }),

  // --------------------------------------------------------------------------
  // STRUCTURALLY DISTINCT #2 — certification gating + delivery logistics.
  preset({
    key: "construction_equipment",
    label: "Construction Equipment",
    tagline: "Excavators, lifts, compactors and skid steers, delivered to site.",
    icon: "excavator",
    adCategory: "construction_industrial",
    terminology: {
      assetSingular: "Machine",
      assetPlural: "Machines",
      assetCode: "Unit #",
      fleet: "Fleet",
      usageLabel: "Engine hours",
      customer: "Account",
      customerPlural: "Accounts",
      checkout: "Dispatch to site",
      checkin: "Return from site",
    },
    assetSchema: [
      { key: "equipment_type", label: "Equipment type", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("excavator", "Excavator"),
          opt("skid_steer", "Skid steer"),
          opt("backhoe", "Backhoe"),
          opt("boom_lift", "Boom lift"),
          opt("scissor_lift", "Scissor lift"),
          opt("telehandler", "Telehandler"),
          opt("compactor_roller", "Compactor / roller"),
          opt("generator", "Generator"),
          opt("compressor", "Compressor"),
          opt("trencher", "Trencher"),
          opt("forklift", "Forklift"),
          opt("dozer", "Dozer"),
        ] },
      { key: "make", label: "Make", type: FIELD_TYPES.TEXT, required: true },
      { key: "model", label: "Model", type: FIELD_TYPES.TEXT, required: true },
      { key: "year", label: "Year", type: FIELD_TYPES.NUMBER },
      { key: "serial_number", label: "Serial number", type: FIELD_TYPES.TEXT, required: true },
      { key: "engine_hours", label: "Engine hours", type: FIELD_TYPES.NUMBER, unit: "hrs", required: true, capturedAt: ["checkout", "checkin"] },
      { key: "operating_weight_kg", label: "Operating weight", type: FIELD_TYPES.NUMBER, unit: "kg",
        help: "Drives transport class and whether a permit is needed." },
      { key: "transport_class", label: "Transport requirement", type: FIELD_TYPES.SELECT,
        options: [
          opt("pickup_trailer", "Pickup + trailer"),
          opt("tilt_deck", "Tilt deck"),
          opt("lowboy", "Lowboy"),
          opt("oversize_permit_required", "Oversize permit required"),
        ] },
      { key: "attachments", label: "Attachments included", type: FIELD_TYPES.CHECKLIST,
        options: [
          opt("bucket", "Bucket"),
          opt("auger", "Auger"),
          opt("breaker_hammer", "Breaker / hammer"),
          opt("forks", "Forks"),
          opt("grapple", "Grapple"),
          opt("blade", "Blade"),
          opt("thumb", "Thumb"),
        ],
        capturedAt: ["checkout", "checkin"] },
      { key: "required_certification", label: "Required operator certification", type: FIELD_TYPES.SELECT, required: true,
        options: [
          opt("none", "None"),
          opt("aerial_lift_mewp", "Aerial lift / MEWP"),
          opt("forklift", "Forklift"),
          opt("excavator", "Excavator"),
          opt("crane_rigging", "Crane / rigging"),
          opt("skid_steer", "Skid steer"),
          opt("fall_protection", "Fall protection"),
        ],
        help: "Blocks checkout unless the operator holds a valid, unexpired certificate." },
      { key: "last_service_hours", label: "Last service at", type: FIELD_TYPES.NUMBER, unit: "hrs" },
      { key: "service_interval_hours", label: "Service interval", type: FIELD_TYPES.NUMBER, unit: "hrs" },
      FUEL_LEVEL_FIELD,
    ],
    usageMetrics: [
      { key: "engine_hours", label: "Engine hours", unit: "hrs", billable: true, includedPerDay: 8, overageCentsPerUnit: 6500 },
    ],
    workflow: {
      idVerificationRequired: true,
      waiverRequired: true,
      depositRequired: true,
      certificationRequired: true,     // hard gate — see rms_customer_certifications
      deliveryEnabled: true,           // delivery + pickup legs are first-class
      insuranceCertificateRequired: true,
      photosRequiredAtCheckout: true,
      photosRequiredAtCheckin: true,
      siteContactRequired: true,
      damageWaiverOffered: true,
    },
    rateDefaults: { primaryUnit: "day", units: ["day", "week", "month"], suggestedDepositCents: 200000 },
  }),
];

// ============================================================================
// Lookup helpers
// ============================================================================

// Operational defaults (turnaround, add-ons, cancellation terms, seasonal rate
// rules, checkout questions) live in a sibling module purely for readability.
// They are merged in here, so every consumer still sees ONE config object.
const PRESETS_BY_KEY = VERTICAL_PRESETS.reduce((acc, p) => {
  acc[p.key] = { ...p, operations: operationalDefaultsFor(p.key) };
  return acc;
}, {});

export const VERTICAL_KEYS = VERTICAL_PRESETS.map((p) => p.key);

/** Returns the preset for a vertical key, or null when unknown. */
export function getPreset(verticalKey) {
  return PRESETS_BY_KEY[verticalKey] || null;
}

/**
 * Resolves the config an operator actually sees: the preset with their
 * config_overrides layered on top. Overrides are shallow-merged per section so
 * an operator can rename one label without restating the whole terminology map.
 */
export function resolveConfig(verticalKey, overrides = {}) {
  const base = getPreset(verticalKey);
  if (!base) return null;

  return {
    ...base,
    terminology: { ...base.terminology, ...(overrides.terminology || {}) },
    workflow: { ...base.workflow, ...(overrides.workflow || {}) },
    rateDefaults: { ...base.rateDefaults, ...(overrides.rateDefaults || {}) },
    operations: { ...base.operations, ...(overrides.operations || {}) },
    // Operators may append custom fields but cannot delete preset fields —
    // removing a required field would orphan data already captured against it.
    assetSchema: [...base.assetSchema, ...(overrides.additionalFields || [])],
  };
}

/** True when the vertical uses continuous occupancy rather than discrete trips. */
export function isContinuous(verticalKey) {
  const p = getPreset(verticalKey);
  return !!p && p.occupancyModel === OCCUPANCY_CONTINUOUS;
}

/** Asset-schema fields captured during a given leg ("checkout" | "checkin"). */
export function fieldsCapturedAt(verticalKey, phase, overrides = {}) {
  const config = resolveConfig(verticalKey, overrides);
  if (!config) return [];
  return config.assetSchema.filter((f) => (f.capturedAt || []).includes(phase));
}

export default VERTICAL_PRESETS;
